const BYTES_PER_MEBIBYTE = 1024 * 1024;

/** Optional browser-reported context, not a measurement of free RAM or CPU speed. */
export interface BrowserPerformanceContext {
  deviceMemoryGiB?: number;
  hardwareConcurrency?: number;
  jsHeapUsedMiB?: number;
  jsHeapAllocatedMiB?: number;
  jsHeapLimitMiB?: number;
  jsHeapUtilizationPercentage?: number;
}

interface NavigatorSnapshot {
  deviceMemory?: number;
  hardwareConcurrency?: number;
}

interface PerformanceSnapshot {
  memory?: {
    usedJSHeapSize?: number;
    totalJSHeapSize?: number;
    jsHeapSizeLimit?: number;
  };
}

/**
 * Reads available values now; callers own the snapshot's timing and lifetime.
 * Keep this to property reads so collecting context does not trigger memory
 * measurement work on the same main thread whose responsiveness we observe.
 */
export function getBrowserPerformanceContext(
  navigatorValue: NavigatorSnapshot = typeof navigator === 'undefined'
    ? {}
    : navigator,
  performanceValue: PerformanceSnapshot = typeof performance === 'undefined'
    ? {}
    : (performance as Performance & PerformanceSnapshot)
): BrowserPerformanceContext {
  const memory = performanceValue.memory;
  // Zero usage is valid, but a zero capacity cannot define heap utilization.
  const usedBytes = nonnegativeFinite(memory?.usedJSHeapSize);
  const allocatedBytes = nonnegativeFinite(memory?.totalJSHeapSize);
  const limitBytes = positiveFinite(memory?.jsHeapSizeLimit);
  const concurrency = positiveFinite(navigatorValue.hardwareConcurrency);

  // Missing APIs mean unknown, not zero. Omit each field independently so a
  // browser with CPU information but no heap API can still contribute context.
  return Object.fromEntries(
    Object.entries({
      deviceMemoryGiB: positiveFinite(navigatorValue.deviceMemory),
      hardwareConcurrency: Number.isInteger(concurrency)
        ? concurrency
        : undefined,
      jsHeapUsedMiB:
        usedBytes == null
          ? undefined
          : Math.round(usedBytes / BYTES_PER_MEBIBYTE),
      jsHeapAllocatedMiB:
        allocatedBytes == null
          ? undefined
          : Math.round(allocatedBytes / BYTES_PER_MEBIBYTE),
      jsHeapLimitMiB:
        limitBytes == null
          ? undefined
          : Math.round(limitBytes / BYTES_PER_MEBIBYTE),
      jsHeapUtilizationPercentage:
        usedBytes == null || limitBytes == null
          ? undefined
          : Math.round((usedBytes / limitBytes) * 10000) / 100,
    }).filter(([, value]) => value != null && Number.isFinite(value))
  );
}

function nonnegativeFinite(value: number | undefined): number | undefined {
  return value != null && Number.isFinite(value) && value >= 0
    ? value
    : undefined;
}

function positiveFinite(value: number | undefined): number | undefined {
  return value != null && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}
